import isFunction from 'lodash/isFunction';
import React, {
  useRef,
  useEffect,
  ReactElement,
  CSSProperties,
} from 'react';
import isNil from 'lodash/isNil';
import clsx from 'clsx';
import {
  useVirtualizer,
  VirtualItem,
} from '@tanstack/react-virtual';
import {
  isWindows,
} from 'core/components/VirtualTable/Header';

const DEFAULT_ESTIMATE_SIZE = 32;

function shouldAdjustScrollPositionOnItemSizeChange(
  item: VirtualItem,
  isScrolling: boolean,
  scrollOffset: number,
) {
  return !isScrolling && item.start < scrollOffset;
}

type Props<T> = {
  renderItemComponent: (props: T, index: number) => ReactElement<T>;
  data: T[];
  scrollToBottom?: boolean;
  overscan?: number;
  className?: string;
  scrollContainerClassName?: string;
  rowClassName?: string | ((item: T) => string);
  gridClassName?: string;
  itemSize?: number;
  scrollbarGutter?: CSSProperties['scrollbarGutter'];
};
export default function VirtualList<T>({
  renderItemComponent,
  data,
  overscan = 5,
  className,
  scrollContainerClassName,
  itemSize = undefined,
  rowClassName,
  gridClassName,
  scrollToBottom = false,
  scrollbarGutter,
}: Props<T>) {
  const parentRef = useRef<HTMLDivElement | null>(null);

  // Detect if we're using grid layout
  const isGridLayout = gridClassName?.includes('grid');

  const virtualizer = useVirtualizer({
    getScrollElement: () => parentRef.current,
    count: data.length,
    estimateSize: () => itemSize ?? DEFAULT_ESTIMATE_SIZE,
    overscan: isGridLayout ? data.length : overscan, // Show all items for grid
  });

  virtualizer.shouldAdjustScrollPositionOnItemSizeChange = (item, _delta, instance) => (
    shouldAdjustScrollPositionOnItemSizeChange(
      item,
      instance.isScrolling,
      parentRef.current?.scrollTop ?? 0,
    )
  );

  const { getVirtualItems, getTotalSize, scrollToOffset } = virtualizer;
  const measureElement = isNil(itemSize) ? virtualizer.measureElement : undefined;

  const virtualItems = getVirtualItems();
  const totalSize = getTotalSize();

  const classes = clsx(
    'relative flex flex-col flex-1 overflow-hidden',
    className,
  );

  const scrollContainerClasses = clsx(
    'populate-virtual-list-scroll relative w-full h-full overflow-y-auto overflow-x-hidden',
    scrollContainerClassName,
  );

  const gridClasses = clsx(
    gridClassName,
    { 'absolute top-0 left-0 w-full': isGridLayout },
  );

  useEffect(() => {
    if (data?.length && scrollToBottom) {
      scrollToOffset(totalSize, { align: 'end' });
    }
  }, [data, totalSize, scrollToBottom]);

  return data.length > 0 ? (
    <div className={classes}>
      <div
        className={scrollContainerClasses}
        ref={parentRef}
        style={{ scrollbarGutter: scrollbarGutter ?? 'stable' }}
      >
        <div
          style={{ height: isGridLayout ? 'auto' : totalSize }}
          className={clsx('w-full', { relative: !isGridLayout })}
        >
          <div
            className={gridClasses}
            style={{
              transform: isGridLayout || !virtualItems[0] ? undefined : `translateY(${virtualItems[0].start}px)`,
            }}
          >
            {virtualItems.map((virtualItem: VirtualItem) => (
              <div
                ref={measureElement}
                key={String(virtualItem.key)}
                data-index={virtualItem.index}
                className={clsx(
                  'w-full relative',
                  isFunction(rowClassName)
                    ? rowClassName(data[virtualItem.index] as T)
                    : rowClassName,
                  { '!w-[calc(100%-32px)]': isNil(scrollbarGutter) && !isWindows },
                )}
                style={{
                  height: !isNil(itemSize) ? `${itemSize}px` : undefined,
                }}
              >
                {renderItemComponent(data[virtualItem.index] as T, virtualItem.index)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  ) : null;
}
